import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {UpdateScope} from '../../../../backend/interfaces/care-service';
import {translations} from '../../../../i18n/translations';
import {FetchEntityUsersResult} from '../../../../hooks/use-fetch-entity-users';
import {useModalBtns} from '../../../modal/use-modal-btns';
import {CareServiceForm, CareServiceItem} from '../data/care-service-item';
import {CareServicesBaseModal} from './care-services-base-modal';
import {
  CareServicesEditConfirmationModal,
  CareServicesDeleteConfirmationModal,
  ConfirmationType,
} from './care-services-confirmation-modals';

export type CareServicesEditModalProps = {
  open: boolean;
  item: CareServiceItem;
  caregivers: FetchEntityUsersResult;
  seniors: FetchEntityUsersResult;
  canWrite?: boolean;
  onCancel: () => void;
  onConfirm: (form: CareServiceForm, updateScope: UpdateScope) => Promise<void>;
  onDelete: (deleteScope: UpdateScope) => Promise<void>;
};

export function CareServicesEditModal(props: CareServicesEditModalProps) {
  const {t} = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saveConfirmation, setSaveConfirmation] = useState<ConfirmationType>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<ConfirmationType>(null);

  const isDisabled = props.canWrite === false;

  const formInstance = useForm<CareServiceForm>({
    mode: 'onChange',
    defaultValues: {
      title: props.item.title,
      color: props.item.color,
      startDate: props.item.startDate,
      endDate: props.item.endDate,
      twentyFourHours: props.item.twentyFourHours,
      repetition: props.item.repetition,
      targetUserId: props.item.targetUserId,
      responsibleUserIds: props.item.responsibleUserIds,
      notes: props.item.notes,
    },
  });

  const actionBtns = useModalBtns({
    left: {
      type: 'cancel',
      disabled: loading,
      onClick: props.onCancel,
    },
    right: {
      type: 'save',
      disabled: loading || isDisabled || formInstance.formState.isDirty === false,
      loading: loading,
      onClick: async () => {
        // Repetition handling
        if (props.item.repetition) {
          setSaveConfirmation('repetition');
        }
        // Single instance handling
        else {
          setSaveConfirmation('single');
        }
      },
    },
  });

  return (
    <>
      <CareServicesBaseModal
        open={props.open}
        title={t(translations.care_services.modals.edit.title)}
        actions={actionBtns}
        form={formInstance}
        caregivers={props.caregivers}
        seniors={props.seniors}
        onDelete={() => {
          // Repetition handling
          if (props.item.repetition) {
            setDeleteConfirmation('repetition');
          }
          // Single instance handling
          else {
            setDeleteConfirmation('single');
          }
        }}
        disabled={isDisabled}
      />

      {/* When Saving Modal */}
      {saveConfirmation && (
        <CareServicesEditConfirmationModal
          confirmationType={saveConfirmation}
          onCancel={() => setSaveConfirmation(null)}
          onConfirm={async (updateScope) => {
            try {
              setLoading(true);
              await props.onConfirm(formInstance.getValues(), updateScope);
            } finally {
              setLoading(false);
            }
          }}
        />
      )}

      {/* When Deleting Modal */}
      {deleteConfirmation && (
        <CareServicesDeleteConfirmationModal
          confirmationType={deleteConfirmation}
          onCancel={() => setDeleteConfirmation(null)}
          onConfirm={async (deleteScope) => {
            try {
              setLoading(true);
              await props.onDelete(deleteScope);
            } finally {
              setLoading(false);
            }
          }}
        />
      )}
    </>
  );
}
